// frontend/src/components/QuickBooking/QuickBookingForm.tsx
// Complete QuickBooking form with simple address input and location picker

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { 
  User, 
  Phone, 
  Mail, 
  Calendar, 
  Clock,
  CheckCircle,
  Loader2,
  Star,
  Sparkles,
  MessageCircle,
  AlertCircle,
  MapPin,
  Building,
  Navigation,
  Globe
} from 'lucide-react';

import { IndianAddress } from '@/types/address';
import type { ServiceDetails } from '@/types/quickBooking';

// Updated booking form data interface to use IndianAddress
interface BookingFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  serviceDate: string;
  preferredTime: string;
  address: IndianAddress;
  description: string;
  contactPreference: 'phone' | 'email' | 'whatsapp';
  urgency: 'normal' | 'urgent' | 'flexible';
  password: string;
  confirmPassword: string;
  termsAccepted: boolean;
}

interface QuickBookingFormProps {
  service: ServiceDetails;
  onSubmit: (formData: BookingFormData) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

// Indian states list
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
];

const QuickBookingForm: React.FC<QuickBookingFormProps> = ({
  service,
  onSubmit,
  isLoading = false,
  error = null
}) => {
  const { user, isAuthenticated } = useAuth();

  // Form state with Indian address
  const [formData, setFormData] = useState<BookingFormData>({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    phoneNumber: user?.phone_number || user?.phone || '',
    serviceDate: '',
    preferredTime: '',
    address: {
      addressLine1: '',
      addressLine2: '',
      landmark: '',
      city: '',
      district: '',
      state: '',
      pincode: '',
      addressType: 'home',
      latitude: undefined,
      longitude: undefined
    },
    description: '',
    contactPreference: 'phone',
    urgency: 'normal',
    password: '',
    confirmPassword: '',
    termsAccepted: false
  });

  // UI state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [addressErrors, setAddressErrors] = useState<Partial<Record<keyof IndianAddress, string>>>({});
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [locationStatus, setLocationStatus] = useState<'none' | 'detecting' | 'success' | 'error'>('none');

  // Helper functions
  const updateFormData = (field: keyof Omit<BookingFormData, 'address'>, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Update address field
  const updateAddress = (field: keyof IndianAddress, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
    
    // Clear address errors when user starts typing
    if (addressErrors[field]) {
      setAddressErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Detect current location and save coordinates
  const detectLocation = async () => {
    setIsDetectingLocation(true);
    setLocationStatus('detecting');
    
    try {
      const position = await getCurrentPosition();
      
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        }
      }));
      
      setLocationStatus('success');
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setLocationStatus('none');
      }, 3000);
      
    } catch (error) {
      console.error('Location detection error:', error);
      setLocationStatus('error');
      
      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        setLocationStatus('none');
      }, 5000);
    } finally {
      setIsDetectingLocation(false);
    }
  };

  // Get current position promise wrapper
  const getCurrentPosition = (): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        resolve,
        (error) => {
          let errorMessage = 'Failed to get location';
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please enable location permissions.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
          }
          
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  };

  // Clear coordinates
  const clearCoordinates = () => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        latitude: undefined,
        longitude: undefined
      }
    }));
    setLocationStatus('none');
  };

  // Get tomorrow's date for minimum service date
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const newAddressErrors: Partial<Record<keyof IndianAddress, string>> = {};

    // Personal info validation
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phoneNumber.replace(/\D/g, ''))) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
    }

    // Service details validation
    if (!formData.serviceDate) {
      newErrors.serviceDate = 'Service date is required';
    } else {
      const selectedDate = new Date(formData.serviceDate);
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      
      if (selectedDate < tomorrow) {
        newErrors.serviceDate = 'Service date must be at least tomorrow';
      }
    }
    if (!formData.preferredTime) newErrors.preferredTime = 'Preferred time is required';

    // Address validation
    if (!formData.address.addressLine1.trim()) {
      newAddressErrors.addressLine1 = 'House/Building number is required';
    }
    if (!formData.address.city.trim()) {
      newAddressErrors.city = 'City is required';
    }
    if (!formData.address.state.trim()) {
      newAddressErrors.state = 'State is required';
    }
    if (!formData.address.pincode.trim()) {
      newAddressErrors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.address.pincode)) {
      newAddressErrors.pincode = 'Please enter a valid 6-digit pincode';
    }

    setErrors(newErrors);
    setAddressErrors(newAddressErrors);
    
    return Object.keys(newErrors).length === 0 && Object.keys(newAddressErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <div className="space-y-8">
      {/* Service Summary Card */}
      <div className="card p-6 bg-gradient-to-r from-primary-50 to-blue-50 border-primary-200">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-primary-600" />
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-gray-900 mb-2">{service.service_name || service.service_name}</h2>
            <p className="text-gray-600 mb-3">{service.description}</p>
            
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center text-warning-600">
                <Star className="w-4 h-4 mr-1 fill-current" />
                <span className="font-medium">{service.provider.rating}</span>
                <span className="text-gray-500 ml-1">({service.provider.total_reviews} reviews)</span>
              </div>
            </div>
          </div>
          
          {/* Provider Info */}
          <div className="text-right">
            <div>
              <p className="text-sm font-medium text-gray-900">{service.provider.business_name}</p>
              <p className="text-sm text-gray-600">{service.provider.city}</p>
            </div>
            <div className="mt-2">
              <p className="text-sm font-medium text-gray-900">Response Time</p>
              <p className="text-sm text-success-600">{service.provider.response_time || '< 2 hours'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Show error if any */}
      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-danger-600 mr-3 flex-shrink-0" />
          <p className="text-danger-700">{error}</p>
        </div>
      )}

      {/* Booking Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="card p-6">
          {/* Personal Information */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2 text-primary-600" />
              Personal Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">
                  First Name <span className="text-danger-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => updateFormData('firstName', e.target.value)}
                  className={`input ${errors.firstName ? 'input-error' : ''}`}
                  placeholder="John"
                  disabled={isAuthenticated}
                  required
                />
                {errors.firstName && <p className="mt-1 text-sm text-danger-600">{errors.firstName}</p>}
              </div>

              <div>
                <label className="label">
                  Last Name <span className="text-danger-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => updateFormData('lastName', e.target.value)}
                  className={`input ${errors.lastName ? 'input-error' : ''}`}
                  placeholder="Doe"
                  disabled={isAuthenticated}
                  required
                />
                {errors.lastName && <p className="mt-1 text-sm text-danger-600">{errors.lastName}</p>}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">
                  Email Address <span className="text-danger-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    className={`input pl-10 ${errors.email ? 'input-error' : ''}`}
                    placeholder="john@example.com"
                    disabled={isAuthenticated}
                    required
                  />
                </div>
                {errors.email && <p className="mt-1 text-sm text-danger-600">{errors.email}</p>}
              </div>

              <div>
                <label className="label">
                  Phone Number <span className="text-danger-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => updateFormData('phoneNumber', e.target.value)}
                    className={`input pl-10 ${errors.phoneNumber ? 'input-error' : ''}`}
                    placeholder="9876543210"
                    disabled={isAuthenticated}
                    required
                  />
                </div>
                {errors.phoneNumber && <p className="mt-1 text-sm text-danger-600">{errors.phoneNumber}</p>}
              </div>
            </div>
          </div>

          {/* Service Details */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-primary-600" />
              Service Details
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">
                  Service Date <span className="text-danger-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    value={formData.serviceDate}
                    onChange={(e) => updateFormData('serviceDate', e.target.value)}
                    className={`input pl-10 ${errors.serviceDate ? 'input-error' : ''}`}
                    min={getTomorrowDate()}
                    required
                  />
                </div>
                {errors.serviceDate && <p className="mt-1 text-sm text-danger-600">{errors.serviceDate}</p>}
              </div>

              <div>
                <label className="label">
                  Preferred Time <span className="text-danger-500">*</span>
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={formData.preferredTime}
                    onChange={(e) => updateFormData('preferredTime', e.target.value)}
                    className={`input pl-10 ${errors.preferredTime ? 'input-error' : ''}`}
                    required
                  >
                    <option value="">Select preferred time</option>
                    <option value="morning">Morning (9 AM - 12 PM)</option>
                    <option value="afternoon">Afternoon (12 PM - 5 PM)</option>
                    <option value="evening">Evening (5 PM - 8 PM)</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
                {errors.preferredTime && <p className="mt-1 text-sm text-danger-600">{errors.preferredTime}</p>}
              </div>
            </div>
          </div>

          {/* Service Location - Embedded Address Form */}
          <div className="mb-8">
            {/* Title and Location Controls */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-primary-600" />
                Service Location
                <span className="text-danger-500 ml-1">*</span>
              </h3>
              
              {/* Location Picker Controls */}
              <div className="flex items-center gap-2">
                {formData.address.latitude && formData.address.longitude && (
                  <button
                    type="button"
                    onClick={clearCoordinates}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Clear Location
                  </button>
                )}
                
                <button
                  type="button"
                  onClick={detectLocation}
                  disabled={isDetectingLocation}
                  className="btn-secondary btn-sm flex items-center gap-2"
                >
                  <Navigation className={`w-4 h-4 ${isDetectingLocation ? 'animate-spin' : ''}`} />
                  {isDetectingLocation ? 'Getting Location...' : 'Get Current Location'}
                </button>
              </div>
            </div>

            {/* Location Status Messages */}
            {locationStatus === 'success' && (
              <div className="flex items-center gap-2 text-sm text-success-600 bg-success-50 px-3 py-2 rounded-lg border border-success-200 mb-4">
                <CheckCircle className="w-4 h-4" />
                <span>Location captured successfully! This will help providers find you easily.</span>
              </div>
            )}

            {locationStatus === 'error' && (
              <div className="flex items-center gap-2 text-sm text-danger-600 bg-danger-50 px-3 py-2 rounded-lg border border-danger-200 mb-4">
                <AlertCircle className="w-4 h-4" />
                <span>Unable to get location. You can continue without it or try again.</span>
              </div>
            )}

            {/* Address Form Fields */}
            <div className="grid grid-cols-1 gap-4">
              {/* Address Type */}
              <div>
                <label className="label">
                  Address Type <span className="text-danger-500">*</span>
                </label>
                <select
                  value={formData.address.addressType}
                  onChange={(e) => updateAddress('addressType', e.target.value as 'home' | 'office' | 'other')}
                  className={`input ${addressErrors.addressType ? 'input-error' : ''}`}
                >
                  <option value="home">Home</option>
                  <option value="office">Office</option>
                  <option value="other">Other</option>
                </select>
                {addressErrors.addressType && <p className="mt-1 text-sm text-danger-600">{addressErrors.addressType}</p>}
              </div>

              {/* Address Line 1 */}
              <div>
                <label className="label">
                  House/Flat No., Building Name <span className="text-danger-500">*</span>
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.address.addressLine1}
                    onChange={(e) => updateAddress('addressLine1', e.target.value)}
                    className={`input pl-10 ${addressErrors.addressLine1 ? 'input-error' : ''}`}
                    placeholder="e.g., 123, ABC Apartments"
                    required
                  />
                </div>
                {addressErrors.addressLine1 && <p className="mt-1 text-sm text-danger-600">{addressErrors.addressLine1}</p>}
              </div>

              {/* Address Line 2 */}
              <div>
                <label className="label">
                  Street, Area, Locality
                </label>
                <input
                  type="text"
                  value={formData.address.addressLine2}
                  onChange={(e) => updateAddress('addressLine2', e.target.value)}
                  className={`input ${addressErrors.addressLine2 ? 'input-error' : ''}`}
                  placeholder="e.g., MG Road, Koramangala"
                />
                {addressErrors.addressLine2 && <p className="mt-1 text-sm text-danger-600">{addressErrors.addressLine2}</p>}
              </div>

              {/* Landmark */}
              <div>
                <label className="label">
                  Landmark (Optional)
                </label>
                <input
                  type="text"
                  value={formData.address.landmark}
                  onChange={(e) => updateAddress('landmark', e.target.value)}
                  className={`input ${addressErrors.landmark ? 'input-error' : ''}`}
                  placeholder="e.g., Near Metro Station, Opposite Mall"
                />
                {addressErrors.landmark && <p className="mt-1 text-sm text-danger-600">{addressErrors.landmark}</p>}
              </div>

              {/* City and District */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">
                    City <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => updateAddress('city', e.target.value)}
                    className={`input ${addressErrors.city ? 'input-error' : ''}`}
                    placeholder="e.g., Bangalore"
                    required
                  />
                  {addressErrors.city && <p className="mt-1 text-sm text-danger-600">{addressErrors.city}</p>}
                </div>

                <div>
                  <label className="label">
                    District
                  </label>
                  <input
                    type="text"
                    value={formData.address.district}
                    onChange={(e) => updateAddress('district', e.target.value)}
                    className={`input ${addressErrors.district ? 'input-error' : ''}`}
                    placeholder="e.g., Bangalore Urban"
                  />
                  {addressErrors.district && <p className="mt-1 text-sm text-danger-600">{addressErrors.district}</p>}
                </div>
              </div>

              {/* State and Pincode */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">
                    State <span className="text-danger-500">*</span>
                  </label>
                  <select
                    value={formData.address.state}
                    onChange={(e) => updateAddress('state', e.target.value)}
                    className={`input ${addressErrors.state ? 'input-error' : ''}`}
                    required
                  >
                    <option value="">Select State</option>
                    {INDIAN_STATES.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  {addressErrors.state && <p className="mt-1 text-sm text-danger-600">{addressErrors.state}</p>}
                </div>

                <div>
                  <label className="label">
                    Pincode <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.address.pincode}
                    onChange={(e) => updateAddress('pincode', e.target.value)}
                    className={`input ${addressErrors.pincode ? 'input-error' : ''}`}
                    placeholder="e.g., 560001"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    required
                  />
                  {addressErrors.pincode && <p className="mt-1 text-sm text-danger-600">{addressErrors.pincode}</p>}
                </div>
              </div>
            </div>

            {/* Address Preview */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
              <p className="text-sm font-medium text-gray-700 mb-1">Address Preview:</p>
              <div className="text-sm text-gray-600">
                {formData.address.addressLine1 && <div>{formData.address.addressLine1}</div>}
                {formData.address.addressLine2 && <div>{formData.address.addressLine2}</div>}
                {formData.address.landmark && <div>Near {formData.address.landmark}</div>}
                <div>
                  {[formData.address.city, formData.address.district, formData.address.state, formData.address.pincode]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              </div>
              
              {/* Show coordinates if available */}
              {formData.address.latitude && formData.address.longitude && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Globe className="w-3 h-3" />
                    <span>
                      Location captured: {formData.address.latitude.toFixed(6)}, {formData.address.longitude.toFixed(6)}
                    </span>
                    <span className="text-success-600 font-medium">✓ This will help providers find you</span>
                  </div>
                </div>
              )}
              
              {/* No coordinates message */}
              {(!formData.address.latitude || !formData.address.longitude) && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-xs text-amber-600">
                    <MapPin className="w-3 h-3" />
                    <span>Click "Get Current Location" to help providers find you easily</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Additional Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MessageCircle className="w-5 h-5 mr-2 text-primary-600" />
              Additional Details
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="label">
                  Special Requirements (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => updateFormData('description', e.target.value)}
                  className="input"
                  placeholder="Any specific requirements, instructions, or details about the service needed"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">
                    Contact Preference
                  </label>
                  <select
                    value={formData.contactPreference}
                    onChange={(e) => updateFormData('contactPreference', e.target.value as 'phone' | 'email' | 'whatsapp')}
                    className="input"
                  >
                    <option value="phone">Phone Call</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="email">Email</option>
                  </select>
                </div>

                <div>
                  <label className="label">
                    Urgency Level
                  </label>
                  <select
                    value={formData.urgency}
                    onChange={(e) => updateFormData('urgency', e.target.value as 'normal' | 'urgent' | 'flexible')}
                    className="input"
                  >
                    <option value="normal">Normal</option>
                    <option value="urgent">Urgent</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="card p-6">
          <button
            type="submit"
            disabled={isLoading}
            className="w-full btn-primary btn-lg flex items-center justify-center space-x-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Submitting Request...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>Book Service</span>
              </>
            )}
          </button>
          
          <p className="text-xs text-gray-500 text-center mt-3">
            By booking this service, you agree to our terms and conditions. 
            Your request will be sent to the service provider for confirmation.
          </p>
          
          {/* Location Benefits */}
          {formData.address.latitude && formData.address.longitude && (
            <div className="mt-3 p-2 bg-success-50 rounded-lg border border-success-200">
              <p className="text-xs text-success-700 text-center">
                ✓ Location captured! This helps providers reach you faster and provide accurate service estimates.
              </p>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default QuickBookingForm;